import PasswordInput from '../components/PasswordInput';
import { formatPhone } from '../utils/employeeFormatters';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon, ChevronLeftIcon, ClockIcon,
  CheckCircleIcon, XCircleIcon, TrashIcon,
} from '@heroicons/react/24/outline';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .oab * { box-sizing:border-box; }
  .oab { font-family:'Sora',sans-serif; color:#1a1d2e; max-width:720px; }
  .oab-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .oab-back:hover { color:#7c3aed; }
  .oab-back svg { width:14px; height:14px; stroke-width:2.5; }
  .oab-title { font-size:1.35rem; font-weight:700; color:#0f1123; margin-bottom:.4rem; }
  .oab-sub   { font-size:.76rem; color:#9094a8; margin-bottom:2rem; }

  /* Notice banner */
  .oab-notice { display:flex; align-items:flex-start; gap:12px; padding:14px 16px; border-radius:12px; background:#fffbeb; border:1px solid #fde68a; margin-bottom:1.5rem; font-size:.78rem; color:#92400e; line-height:1.55; }
  .oab-notice svg { width:18px; height:18px; flex-shrink:0; color:#f59e0b; stroke-width:1.8; margin-top:1px; }

  .oab-card  { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.8rem; margin-bottom:1rem; }
  .oab-section { font-size:.84rem; font-weight:700; color:#0f1123; margin-bottom:1.2rem; padding-bottom:10px; border-bottom:1px solid #f0f2f8; display:flex; align-items:center; gap:8px; }
  .oab-section svg { width:16px; height:16px; stroke:#7c3aed; }
  .oab-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .oab-field { display:flex; flex-direction:column; gap:5px; }
  .oab-field.full { grid-column:1/-1; }
  .oab-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .oab-input { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .oab-input:focus { border-color:#7c3aed; background:white; box-shadow:0 0 0 3px rgba(124,58,237,.08); }
  .oab-hint  { font-size:.67rem; color:#b0b5cc; }
  .oab-btn   { display:inline-flex; align-items:center; gap:8px; padding:11px 24px; border:none; border-radius:11px; background:#7c3aed; color:white; font-family:'Sora',sans-serif; font-size:.86rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .oab-btn:hover { background:#6d28d9; box-shadow:0 4px 16px rgba(124,58,237,.35); transform:translateY(-1px); }
  .oab-btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; box-shadow:none !important; }

  /* Requests list */
  .req-section-title { font-size:.92rem; font-weight:700; color:#0f1123; margin:2rem 0 .9rem; display:flex; align-items:center; gap:8px; }
  .req-list { display:flex; flex-direction:column; gap:.7rem; }
  .req-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.2rem; display:flex; align-items:flex-start; gap:14px; transition:box-shadow .15s; }
  .req-card:hover { box-shadow:0 3px 14px rgba(0,0,0,.06); }
  .req-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .req-icon svg { width:18px; height:18px; stroke-width:1.8; }
  .req-icon.pending  { background:#fffbeb; color:#f59e0b; }
  .req-icon.approved { background:#ecfdf5; color:#10c98e; }
  .req-icon.rejected { background:#fef2f2; color:#ef4444; }
  .req-name  { font-size:.88rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .req-meta  { font-size:.7rem; color:#9094a8; margin-bottom:4px; }
  .req-badge { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:700; padding:3px 9px; border-radius:99px; }
  .req-badge.pending  { background:#fffbeb; color:#f59e0b; }
  .req-badge.approved { background:#ecfdf5; color:#10c98e; }
  .req-badge.rejected { background:#fef2f2; color:#ef4444; }
  .req-note { font-size:.73rem; color:#6b7194; margin-top:5px; background:#f8f9fc; border-radius:8px; padding:6px 10px; border-left:3px solid #e0e4f0; }
  .req-note.rejected { border-left-color:#ef4444; background:#fef2f2; color:#991b1b; }
  .req-note.approved { border-left-color:#10c98e; background:#ecfdf5; color:#065f46; }
  .req-cancel { padding:5px 12px; border:1.5px solid #fecaca; border-radius:8px; background:#fef2f2; color:#ef4444; font-family:'Sora',sans-serif; font-size:.72rem; font-weight:600; cursor:pointer; transition:all .15s; display:flex; align-items:center; gap:5px; flex-shrink:0; }
  .req-cancel:hover { background:#ef4444; color:white; }
  .req-cancel svg { width:13px; height:13px; stroke-width:2.5; }
  .req-cancel:disabled { opacity:.5; cursor:not-allowed; }
  .oab-loading { display:flex; align-items:center; justify-content:center; height:100px; gap:10px; color:#9094a8; font-size:.82rem; }
  .oab-spin { width:16px; height:16px; border:2px solid #e4e7f0; border-top-color:#7c3aed; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  @media (max-width:600px) { .oab-grid { grid-template-columns:1fr; } .oab-field.full { grid-column:1; } }
`;

const STATUS_ICON = {
  pending:  ClockIcon,
  approved: CheckCircleIcon,
  rejected: XCircleIcon,
};

export default function OwnerAddBranch() {
  const navigate  = useNavigate();
  const [saving,   setSaving]   = useState(false);
  const [requests, setRequests] = useState([]);
  const [reqLoad,  setReqLoad]  = useState(true);
  const [cancelling, setCancelling] = useState({});
  const [form, setForm] = useState({
    name:'', address:'', phone:'', email:'',
    adminName:'', adminEmail:'', adminPassword:'',
  });

  const set = k => e => setForm(p => ({...p, [k]: e.target.value}));

  // Load existing requests
  useEffect(() => {
    api.get('/owner/branch-requests')
      .then(r => setRequests(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setReqLoad(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name)          return toast.error('Branch name is required');
    if (!form.adminEmail)    return toast.error('Admin email is required');
    if (!form.adminPassword) return toast.error('Admin password is required');
    if (form.adminPassword.length < 6) return toast.error('Admin password must be at least 6 characters');
    setSaving(true);
    try {
      const { data } = await api.post('/owner/branch-requests', form);
      toast.success('Branch request submitted! Awaiting super admin approval.');
      setRequests(p => [data, ...p]);
      setForm({ name:'', address:'', phone:'', email:'', adminName:'', adminEmail:'', adminPassword:'' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    } finally { setSaving(false); }
  };

  const handleCancel = async (reqId) => {
    if (!window.confirm('Cancel this branch request?')) return;
    setCancelling(p => ({...p, [reqId]: true}));
    try {
      await api.delete(`/owner/branch-requests/${reqId}`);
      toast.success('Request cancelled');
      setRequests(p => p.filter(r => r._id !== reqId));
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setCancelling(p => ({...p, [reqId]: false})); }
  };

  const pendingCount  = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;

  return (
    <div className="oab"><style>{css}</style>

      <button className="oab-back" onClick={() => navigate('/owner/branches')}>
        <ChevronLeftIcon />Back to Branches
      </button>

      <div className="oab-title">Request New Branch</div>
      <div className="oab-sub">
        Submit a request to add a new school branch. Super admin will review and approve it.
        {approvedCount > 0 && ` · ${approvedCount} branch${approvedCount!==1?'es':''} already approved.`}
      </div>

      {/* Notice */}
      <div className="oab-notice">
        <ClockIcon />
        <div>
          <strong>Approval required.</strong> New branches require super admin approval before they go live.
          You'll see the status of your request below. Once approved, the branch and admin account will be created automatically.
          {pendingCount > 0 && <div style={{marginTop:4}}>You have <strong>{pendingCount} pending</strong> request{pendingCount!==1?'s':''}.</div>}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="oab-card">
          <div className="oab-section"><BuildingOfficeIcon />School Information</div>
          <div className="oab-grid">
            <div className="oab-field full">
              <label className="oab-label">School Name *</label>
              <input className="oab-input" placeholder="e.g. Bright Future School — Branch 2" value={form.name} onChange={set('name')} required />
            </div>
            <div className="oab-field full">
              <label className="oab-label">Address</label>
              <input className="oab-input" placeholder="Street, City" value={form.address} onChange={set('address')} />
            </div>
            <div className="oab-field">
              <label className="oab-label">Phone</label>
              <input className="oab-input" placeholder="+92 300 0000000" value={form.phone} onChange={e => setForm(p => ({...p, phone: formatPhone(e.target.value)}))}  />
            </div>
            <div className="oab-field">
              <label className="oab-label">School Email</label>
              <input type="email" className="oab-input" placeholder="school@email.com" value={form.email} onChange={set('email')} />
            </div>
          </div>
        </div>

        <div className="oab-card">
          <div className="oab-section" style={{color:'#4f46e5'}}>
            <BuildingOfficeIcon style={{stroke:'#4f46e5'}}/>Admin Account
          </div>
          <div className="oab-sub" style={{marginTop:'-8px',marginBottom:'14px',fontSize:'.74rem'}}>
            This admin account will be created automatically once the branch is approved.
          </div>
          <div className="oab-grid">
            <div className="oab-field full">
              <label className="oab-label">Admin Name</label>
              <input className="oab-input" placeholder="e.g. Branch Manager" value={form.adminName} onChange={set('adminName')} />
            </div>
            <div className="oab-field">
              <label className="oab-label">Admin Email *</label>
              <input type="email" className="oab-input" placeholder="admin@branch.com" value={form.adminEmail} onChange={set('adminEmail')} required />
            </div>
            <div className="oab-field">
              <label className="oab-label">Admin Password *</label>
              <PasswordInput className="oab-input" placeholder="Min 6 characters" value={form.adminPassword} onChange={set('adminPassword')} required />
              <span className="oab-hint">Keep this safe — share with the branch admin after approval</span>
            </div>
          </div>
        </div>

        <button type="submit" className="oab-btn" disabled={saving}>
          <ClockIcon style={{width:16,height:16,strokeWidth:2}}/>
          {saving ? 'Submitting Request…' : 'Submit for Approval'}
        </button>
      </form>

      {/* Existing requests */}
      <div className="req-section-title">
        <ClockIcon style={{width:16,height:16,color:'#9094a8'}}/>
        Your Branch Requests
        {requests.length > 0 && <span style={{fontSize:'.72rem',fontWeight:600,color:'#9094a8',background:'#f0f2f7',padding:'2px 8px',borderRadius:99}}>{requests.length}</span>}
      </div>

      {reqLoad ? (
        <div className="oab-loading"><div className="oab-spin"/>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={{background:'white',borderRadius:12,border:'1px solid #e8ebf4',padding:'1.5rem',textAlign:'center',color:'#9094a8',fontSize:'.78rem'}}>
          No branch requests yet.
        </div>
      ) : (
        <div className="req-list">
          {requests.map(r => {
            const Icon = STATUS_ICON[r.status] || ClockIcon;
            return (
              <div key={r._id} className="req-card">
                <div className={`req-icon ${r.status}`}><Icon /></div>
                <div style={{flex:1,minWidth:0}}>
                  <div className="req-name">{r.name}</div>
                  <div className="req-meta">
                    {r.address && `${r.address} · `}
                    Admin: {r.adminEmail} · Submitted: {fmtDate(r.createdAt)}
                  </div>
                  <span className={`req-badge ${r.status}`}>
                    {r.status === 'pending'  && '⏳ Pending Approval'}
                    {r.status === 'approved' && '✓ Approved'}
                    {r.status === 'rejected' && '✗ Rejected'}
                  </span>
                  {r.status === 'approved' && (
                    <div className={`req-note approved`}>
                      ✓ Branch created on {fmtDate(r.reviewedAt)} by {r.reviewedBy}.
                      {r.superNote && ` Note: ${r.superNote}`}
                    </div>
                  )}
                  {r.status === 'rejected' && (
                    <div className={`req-note rejected`}>
                      ✗ Rejected on {fmtDate(r.reviewedAt)} by {r.reviewedBy}.
                      {r.rejectReason && ` Reason: ${r.rejectReason}`}
                    </div>
                  )}
                  {r.status === 'pending' && r.superNote && (
                    <div className="req-note">{r.superNote}</div>
                  )}
                </div>
                {r.status === 'pending' && (
                  <button className="req-cancel" disabled={cancelling[r._id]} onClick={() => handleCancel(r._id)}>
                    <TrashIcon />{cancelling[r._id] ? '…' : 'Cancel'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}